import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useLocation } from 'react-router-dom'
import { Button } from '@/components/ui/button'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { cn } from '@/lib/utils'
import {
  DataLayoutScopeProvider,
  useDataLayout,
  useResolvedDataLayout,
} from '@/utils/DataLayoutContext'
import { useUIVersion } from '@/utils/UIVersionContext'
import {
  ChevronDown,
  ChevronsDownUp,
  ChevronsUpDown,
  ChevronUp,
  Form,
  LayoutDashboard,
} from 'lucide-react'

import { Body1 } from '../ui/typography'

interface DataCardProps extends PropsWithChildren {
  title?: string
  columns?: 1 | 2 | 3
  className?: string
  boxed?: boolean
}

const countRenderableChildren = (children: React.ReactNode): number =>
  React.Children.toArray(children).reduce<number>((count, child) => {
    if (
      React.isValidElement<{ children?: React.ReactNode }>(child) &&
      child.type === React.Fragment
    ) {
      return count + countRenderableChildren(child.props.children)
    }

    return count + 1
  }, 0)

type DataCardExpansionContextType = {
  allExpanded: boolean
  canCollapse: boolean
  hasCards: boolean
  isCardExpanded: (id: string) => boolean
  registerCard: (id: string) => void
  setCardExpanded: (id: string, expanded: boolean) => void
  toggleAll: () => void
  unregisterCard: (id: string) => void
}

type CardExpansionState = Record<string, boolean>

const DATA_CARD_SESSION_STORAGE_PREFIX = 'data-card-expansion'

const readStoredCardStates = (storageKey?: string): CardExpansionState => {
  if (!storageKey || typeof window === 'undefined') return {}

  try {
    const rawValue = window.sessionStorage.getItem(storageKey)
    if (!rawValue) return {}
    const parsedValue = JSON.parse(rawValue)

    if (!parsedValue || typeof parsedValue !== 'object' || Array.isArray(parsedValue)) {
      return {}
    }

    return Object.entries(parsedValue).reduce<CardExpansionState>((nextState, [cardId, value]) => {
      if (typeof value === 'boolean') nextState[cardId] = value
      return nextState
    }, {})
  } catch {
    return {}
  }
}

const DataCardExpansionContext = createContext<DataCardExpansionContextType | undefined>(undefined)

const DataCardExpansionProvider: React.FC<
  PropsWithChildren<{ canCollapse: boolean; storageKey?: string }>
> = ({ children, canCollapse, storageKey }) => {
  const [cardStates, setCardStates] = useState<CardExpansionState>(() =>
    readStoredCardStates(storageKey)
  )
  const storedCardStatesRef = useRef<CardExpansionState>(cardStates)
  const shouldPersistRef = useRef(false)

  const persistCardStates = useCallback(
    (nextCardStates: CardExpansionState) => {
      if (!storageKey || typeof window === 'undefined') {
        return
      }

      try {
        const mergedCardStates = {
          ...storedCardStatesRef.current,
          ...nextCardStates,
        }

        storedCardStatesRef.current = mergedCardStates
        window.sessionStorage.setItem(storageKey, JSON.stringify(mergedCardStates))
      } catch {
        // ignore, not important
      }
    },
    [storageKey]
  )

  useEffect(() => {
    if (!canCollapse) {
      setCardStates((current) =>
        Object.keys(current).reduce<CardExpansionState>((nextState, cardId) => {
          nextState[cardId] = true
          return nextState
        }, {})
      )
    }
  }, [canCollapse])

  useEffect(() => {
    if (!shouldPersistRef.current) {
      return
    }

    shouldPersistRef.current = false
    persistCardStates(cardStates)
  }, [cardStates, persistCardStates])

  const registerCard = useCallback((id: string) => {
    setCardStates((current) => {
      if (id in current) return current

      return {
        ...current,
        [id]: storedCardStatesRef.current[id] ?? true,
      }
    })
  }, [])

  const unregisterCard = useCallback((id: string) => {
    setCardStates((current) => {
      if (!(id in current)) return current

      const nextState = { ...current }
      delete nextState[id]
      return nextState
    })
  }, [])

  const setCardExpanded = useCallback((id: string, expanded: boolean) => {
    shouldPersistRef.current = true

    setCardStates((current) => {
      if (current[id] === expanded) return current

      return {
        ...current,
        [id]: expanded,
      }
    })
  }, [])

  const isCardExpanded = useCallback((id: string) => cardStates[id] ?? true, [cardStates])
  const hasCards = Object.keys(cardStates).length > 0
  const allExpanded = Object.values(cardStates).every(Boolean)

  const toggleAll = useCallback(() => {
    shouldPersistRef.current = true

    setCardStates((current) => {
      const nextExpanded = !Object.values(current).every(Boolean)

      return Object.keys(current).reduce<CardExpansionState>((nextState, cardId) => {
        nextState[cardId] = nextExpanded
        return nextState
      }, {})
    })
  }, [])

  const contextValue = useMemo(
    () => ({
      allExpanded,
      canCollapse,
      hasCards,
      isCardExpanded,
      registerCard,
      setCardExpanded,
      toggleAll,
      unregisterCard,
    }),
    [
      allExpanded,
      canCollapse,
      hasCards,
      isCardExpanded,
      registerCard,
      setCardExpanded,
      toggleAll,
      unregisterCard,
    ]
  )

  return (
    <DataCardExpansionContext.Provider value={contextValue}>
      {children}
    </DataCardExpansionContext.Provider>
  )
}

export const DataCard: React.FC<DataCardProps> = ({
  children,
  boxed,
  title,
  columns = 1,
  className,
}) => {
  const { isNewUI } = useUIVersion()
  const { activeType } = useResolvedDataLayout()
  const expansion = useContext(DataCardExpansionContext)
  const generatedCardId = useId()
  const cardId = useMemo(() => {
    if (typeof title === 'string' && title.trim()) {
      return `title:${title.trim()}`
    }

    if (typeof title === 'number') {
      return `title:${title}`
    }

    return generatedCardId
  }, [generatedCardId, title])
  const isCollapsible = expansion?.canCollapse ?? false

  useLayoutEffect(() => {
    if (!expansion) return undefined

    expansion.registerCard(cardId)

    return () => {
      expansion.unregisterCard(cardId)
    }
  }, [cardId, expansion?.registerCard, expansion?.unregisterCard])

  const resolvedColumns = activeType === 'list' ? 1 : columns
  const items = useMemo(() => React.Children.toArray(children), [children])
  const itemCount = useMemo(() => countRenderableChildren(children), [children])
  const canCollapse = isCollapsible && activeType === 'grid'
  const isExpanded = canCollapse ? (expansion?.isCardExpanded(cardId) ?? true) : true
  const hasTitle = Boolean(title)

  const rows = Math.ceil(itemCount / resolvedColumns)
  return (
    <section
      className={cn(
        'relative flex min-h-0 flex-col bg-card p-4',
        (hasTitle || canCollapse) && 'pt-7',
        activeType === 'grid' || boxed
          ? 'rounded-lg border border-border shadow-sm'
          : 'border-0 border-t border-border rounded-none shadow-none',
        className
      )}
    >
      {hasTitle && (
        <div
          onClick={() => expansion?.setCardExpanded(cardId, !isExpanded)}
          className={cn(
            'cursor-pointer transition-[top] absolute left-4 top-0 z-10 -translate-y-1/2',
            !isExpanded && 'top-5.5',
            !isNewUI && '-top-0.5',
            canCollapse && 'cursor-pointer'
          )}
        >
          <div
            className={cn(
              'inline-flex max-w-full items-center bg-background/95 py-1 px-1 supports-backdrop-filter:bg-background/80 dark:border-border/80 dark:bg-background/85',
              isNewUI && 'rounded-full border border-border/70 backdrop-blur-sm shadow-sm px-3'
            )}
          >
            <Body1 bold className='block truncate text-foreground/95'>
              {title}
            </Body1>
          </div>
        </div>
      )}

      {canCollapse && (
        <Button
          aria-label={isExpanded ? 'Collapse card' : 'Expand card'}
          className='absolute right-0 top-0 z-10 rounded-none rounded-bl-sm rounded-tr-md border-t-0 border-r-0'
          onClick={() => expansion?.setCardExpanded(cardId, !isExpanded)}
          size='xs'
          title={isExpanded ? 'Collapse card' : 'Expand card'}
          type='button'
          variant='outline'
        >
          {isExpanded ? <ChevronUp className='size-4' /> : <ChevronDown className='size-4' />}
          <span className='text-xs font-medium'>{isExpanded ? 'Collapse' : 'Expand'}</span>
        </Button>
      )}

      <div
        className={cn(
          'grid transition-[grid-template-rows,opacity] duration-300 ease-out motion-reduce:transition-none',
          canCollapse
            ? isExpanded
              ? 'grid-rows-[1fr] opacity-100'
              : 'pointer-events-none grid-rows-[0fr] opacity-0'
            : 'grid-rows-[1fr] opacity-100'
        )}
      >
        <div className='min-h-0'>
          <div
            className='grid w-full flex-1 content-start gap-x-4 gap-y-3'
            style={{
              gridAutoFlow: 'column',
              gridTemplateRows: `repeat(${rows}, minmax(0, auto))`,
              gridTemplateColumns: `repeat(${resolvedColumns}, minmax(0, 1fr))`,
            }}
          >
            {items}
          </div>
        </div>
      </div>
    </section>
  )
}

interface DataGridProps extends PropsWithChildren {
  rightAddon?: React.ReactNode
  columns?: 1 | 2
}

export const DataGrid: React.FC<DataGridProps> = (props) => {
  return (
    <DataLayoutScopeProvider>
      <ScopedDataGridContent {...props} />
    </DataLayoutScopeProvider>
  )
}

const ScopedDataGridContent: React.FC<DataGridProps> = (props) => {
  const { isGridAvailable } = useDataLayout()
  const location = useLocation()
  const storageKey = `${DATA_CARD_SESSION_STORAGE_PREFIX}:${location.pathname}`

  return (
    <DataCardExpansionProvider
      key={storageKey}
      canCollapse={isGridAvailable}
      storageKey={storageKey}
    >
      <DataGridContent {...props} />
    </DataCardExpansionProvider>
  )
}

const DataGridContent: React.FC<DataGridProps> = (props) => {
  const { children, rightAddon, columns = 2 } = props
  const { activeType, isGridAvailable, setActiveType } = useDataLayout()
  const expansion = useContext(DataCardExpansionContext)
  const columnCount = Math.min(Math.max(countRenderableChildren(children), 1), columns)
  const showToolbar = isGridAvailable || Boolean(rightAddon)

  return (
    <div className='flex flex-col gap-4 py-4'>
      {showToolbar && (
        <div
          className={cn(
            'flex items-center gap-4',
            isGridAvailable && rightAddon
              ? 'justify-between'
              : isGridAvailable
                ? 'justify-start'
                : 'justify-end'
          )}
        >
          {isGridAvailable && (
            <div className='flex flex-wrap items-center gap-2'>
              <ToggleGroup variant='outline' defaultValue={[activeType]}>
                <ToggleGroupItem
                  value='grid'
                  onClick={() => setActiveType('grid')}
                  aria-label='Grid'
                >
                  <LayoutDashboard />
                  Grid
                </ToggleGroupItem>
                <ToggleGroupItem
                  value='list'
                  onClick={() => setActiveType('list')}
                  aria-label='List'
                >
                  <Form />
                  List
                </ToggleGroupItem>
              </ToggleGroup>

              {activeType === 'grid' && expansion?.hasCards && (
                <Button onClick={expansion.toggleAll} size='md' type='button' variant='outline'>
                  {expansion.allExpanded ? <ChevronsDownUp /> : <ChevronsUpDown />}
                </Button>
              )}
            </div>
          )}

          {rightAddon}
        </div>
      )}
      <div
        className='grid gap-4'
        style={{ gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))` }}
      >
        {children}
      </div>
    </div>
  )
}

interface DataColumnProps extends PropsWithChildren {
  colSpan?: 1 | 2
}

export const DataColumn: React.FC<DataColumnProps> = ({ children, colSpan = 1 }) => (
  <div className={`flex flex-col gap-4 ${colSpan === 2 ? 'col-span-2' : ''}`}>{children}</div>
)
